import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Button, Card, Col, Form, Row, Modal } from "react-bootstrap";
import ISalesZone, { ISalesZoneCreateOrUpdate, ISalesZoneInitResult } from "models/sql/ISalesZone";
import SalesZoneService from "services/SalesZoneService";
import IDealer from "models/sql/IDealer";
import DealerService from "services/DealerService";
import FireDepartmentService from "services/FireDepartmentService";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";
import MapControl, { IAzureMapMasterComponentHtmlMarkerOptions, IAzureMapMasterComponentPin } from "components/MapControl";
import ISalesRep from "models/sql/ISalesRep";
import SalesRepService from "services/SalesRepService";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";

interface ISalesZonesDetailProps {
    baseData?: ISalesZoneInitResult;
    setForceRefreshBaseData?: (data: Date) => void
}

const SalesZonesDetail = (props: ISalesZonesDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [draggablePinPosition, setDraggablePinPosition] = useState<{ longitude: number, latitude: number }>();
    const [pins, setPins] = useState<IAzureMapMasterComponentPin[]>([]);
    const [htmlMarkers, setHtmlMarkers] = useState<IAzureMapMasterComponentHtmlMarkerOptions[]>([]);
    const htmlMarkersRef = useRef(htmlMarkers);
    htmlMarkersRef.current = htmlMarkers;
    const [mapRadiusMiles, setMapRadiusMiles] = useState(50);
    const [mapMaxRecords, setMapMaxRecords] = useState(500);
    const [name, setName] = useState("Sales Zone");
    const [departments, setDepartments] = useState<IFireDepartment[]>([]);
    const [filters, setFilters] = useState<IFireDepartmentFilters>({});
    const [settingsModalShowing, setSettingsModalShowing] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(new Date());
    const [loadComplete, setLoadComplete] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [zoomLevel, setZoomLevel] = useState<number>();
    const [defaultZoomLevel, setDefaultZoomLevel] = useState<number>();

    const priorities = ["Target", "Contact Made", "Follow-Up Need", "Rep Hand-Off", "Active"];
    const actions = ["AQ Test", "Demo", "Digital Support", "Find Contact", "F2F Meet", "Quote", "Virtual Meet"];
    const statuses = ["Disqualified", "In-Progress", "Needed", "Sent", "Scheduled"];

    const [dealer, setDealer] = useState<IDealer>();
    const [salesRep, setSalesRep] = useState<ISalesRep>();
    const { id }: any = useParams();
    const [salesZone, setSalesZone] = useState<ISalesZone | null>({
        salesZoneId: -1,
        name: "New Sales Zone",
        salesRepId: undefined,
        dealerId: undefined,
        longitude: -83.80657,
        latitude: 43.51072,
        distance: 100,
        zoomLevel: 6,
        country: undefined,
        created: undefined,
        systemDataJson: undefined,
        notes: undefined,
        types: undefined,
        priorities: undefined,
        statuses: undefined,
        actions: undefined,
        specializations: undefined,
        deptTypes: undefined,
        organizationTypes: undefined,
        minNumberOfStations: undefined,
        maxNumberOfStations: undefined,
        minActiveFirefightersCareer: undefined,
        maxActiveFirefightersCareer: undefined,
        minActiveFirefightersVolunteer: undefined,
        maxActiveFirefightersVolunteer: undefined,
        minActiveFirefightersPaidPerCall: undefined,
        maxActiveFirefightersPaidPerCall: undefined,
        minEquipment: undefined,
        maxEquipment: undefined,
        minHRAs: undefined,
        maxHRAs: undefined,
        minAirCards: undefined,
        maxAirCards: undefined,
        primaryAgencyForEmergencyMgmt: undefined
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    setLoadingLocations(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await SalesZoneService.GetAsync(accessToken, id);

                    if (result.record) {
                        setSalesZone(result.record);

                        setFilters({
                            priorities: result.record?.priorities?.split(","),
                            statuses: result.record?.statuses?.split(","),
                            actions: result.record?.actions?.split(","),
                            specializations: result.record?.specializations?.split(","),
                            deptTypes: result.record?.deptTypes?.split(","),
                            organizationTypes: result.record?.organizationTypes?.split(","),
                            minNumberOfStations: result.record?.minNumberOfStations,
                            maxNumberOfStations: result.record?.maxNumberOfStations,
                            minActiveFirefightersCareer: result.record?.minActiveFirefightersCareer,
                            maxActiveFirefightersCareer: result.record?.maxActiveFirefightersCareer,
                            minActiveFirefightersVolunteer: result.record?.minActiveFirefightersVolunteer,
                            maxActiveFirefightersVolunteer: result.record?.maxActiveFirefightersVolunteer,
                            minActiveFirefightersPaidPerCall: result.record?.minActiveFirefightersPaidPerCall,
                            maxActiveFirefightersPaidPerCall: result.record?.maxActiveFirefightersPaidPerCall,
                            minEquipment: result.record?.minEquipment,
                            maxEquipment: result.record?.maxEquipment,
                            minHRAs: result.record?.minHRAs,
                            maxHRAs: result.record?.maxHRAs,
                            minAirCards: result.record?.minAirCards,
                            maxAirCards: result.record?.maxAirCards,
                            primaryAgencyForEmergencyMgmt: result.record?.primaryAgencyForEmergencyMgmt
                        });

                        if (result.record.zoomLevel) {
                            setZoomLevel(result.record.zoomLevel);
                            setDefaultZoomLevel(result.record.zoomLevel);
                        }

                        window.setTimeout(() => {
                            if (result?.record && result.record.latitude && result.record.longitude) {
                                setDraggablePinPosition({
                                    latitude: result.record.latitude,
                                    longitude: result.record.longitude
                                });
                            }
                        }, 300);

                        if (result.record.distance) {
                            setMapRadiusMiles(result.record.distance);
                        }

                        if (result.record.dealerId !== null && result.record.dealerId !== undefined) {
                            const dealerResult = await DealerService.GetAsync(accessToken, result.record.dealerId);

                            if (dealerResult.record) {
                                setDealer(dealerResult.record);
                            }
                        }

                        if (result.record.salesRepId !== null && result.record.salesRepId !== undefined) {
                            const salesRepResult = await SalesRepService.GetAsync(accessToken, result.record.salesRepId);

                            if (salesRepResult.record) {
                                setSalesRep(salesRepResult.record);
                            }
                        }
                    }
                    else {
                        setSalesZone(null);
                        globalContext.showErrorMessage("Unable to load Sales Zone");
                    }

                    setLoadingLocations(false);
                    setLoadComplete(true);
                }
                else {
                    setZoomLevel(7);
                    setDefaultZoomLevel(7);
                    setTimeout(() => {
                        setDraggablePinPosition({
                            latitude: 41.50607,
                            longitude: -87.23822
                        });
                    }, 300);
                    setLoadComplete(true);
                }
            })();
        }, [id]);

    useEffect(() => {
        (async () => {
            if (loadComplete && draggablePinPosition && draggablePinPosition.latitude && draggablePinPosition.longitude) {
                setLoadingLocations(true);
                const accessToken = await globalContext.getAccessTokenAsync();
                const result =
                    await FireDepartmentService.GetByDistanceComplexAsync(
                        accessToken,
                        draggablePinPosition.latitude,
                        draggablePinPosition.longitude,
                        mapRadiusMiles,
                        filters,
                        0,
                        mapMaxRecords);

                if (result.records) {
                    setDepartments(result.records);
                }
                else {
                    setDepartments([]);
                    globalContext.showErrorMessage("Unable to load Fire Department");
                }

                setLoadingLocations(false);
            }
        })();
    },
        [forceUpdate, loadComplete, draggablePinPosition]);

    useEffect(() => {
        let newPins: IAzureMapMasterComponentPin[] = [];
        if (departments && departments.length > 0) {

            newPins =
                departments
                    .filter((department) => {
                        return department.hqlatitude && department.hqlongitude;
                    }).map(
                        (department) => ({
                            latitude: department.hqlatitude ?? 0,
                            longitude: department.hqlongitude ?? 0,
                            title: department.fireDeptName ?? ""
                        }));

        }

        setPins(newPins);
    }, [departments]);

    const updateSettings = () => {
        setForceUpdate(new Date());
    };

    const mapClickCallback = (latitude: number, longitude: number) => {
        console.log('Clicked on:', latitude, longitude);
        setHtmlMarkers([]);
    };

    const pinDragCallback = (latitude: number, longitude: number) => {
        setDraggablePinPosition({ latitude, longitude });
        updateSettings();
    };

    const pinClickCallback = (marker: any) => {
        console.log('Marker clicked:', marker);
    };

    const zoomCallback = (zoom: number) => {
        console.log("Updating zoom level to: ", zoom);
        setZoomLevel(zoom);
    };

    const deleteAsync = async () => {
        if (salesZone && salesZone.salesZoneId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await SalesZoneService.DeleteAsync(accessToken, salesZone.salesZoneId);
            globalContext.setLoading(false);
            navigate("/SalesZones");
        }
    };

    const submitFormAsync = async () => {
        if (salesZone) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: ISalesZoneCreateOrUpdate = {
                salesZoneId: salesZone.salesZoneId,
                name: name,
                salesRepId: salesRep?.salesRepId,
                dealerId: dealer?.dealerId,
                longitude: draggablePinPosition?.longitude || 0,
                latitude: draggablePinPosition?.latitude || 0,
                distance: mapRadiusMiles,
                zoomLevel: zoomLevel,
                country: salesZone.country,
                created: salesZone.created,
                systemDataJson: salesZone.systemDataJson,
                notes: salesZone.notes,
                types: salesZone.types,
                priorities: filters?.priorities?.join(","),
                statuses: filters?.statuses?.join(","),
                actions: filters?.actions?.join(","),
                specializations: filters?.specializations?.join(","),
                deptTypes: filters?.deptTypes?.join(","),
                organizationTypes: filters?.organizationTypes?.join(","),
                minNumberOfStations: filters?.minNumberOfStations,
                maxNumberOfStations: filters?.maxNumberOfStations,
                minActiveFirefightersCareer: filters?.minActiveFirefightersCareer,
                maxActiveFirefightersCareer: filters?.maxActiveFirefightersCareer,
                minActiveFirefightersVolunteer: filters?.minActiveFirefightersVolunteer,
                maxActiveFirefightersVolunteer: filters?.maxActiveFirefightersVolunteer,
                minActiveFirefightersPaidPerCall: filters?.minActiveFirefightersPaidPerCall,
                maxActiveFirefightersPaidPerCall: filters?.maxActiveFirefightersPaidPerCall,
                minEquipment: filters?.minEquipment,
                maxEquipment: filters?.maxEquipment,
                minHRAs: filters?.minHRAs,
                maxHRAs: filters?.maxHRAs,
                minAirCards: filters?.minAirCards,
                maxAirCards: filters?.maxAirCards,
                primaryAgencyForEmergencyMgmt: filters?.primaryAgencyForEmergencyMgmt
            };

            await SalesZoneService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.salesZoneId === -1) {
                if (props.setForceRefreshBaseData) {
                    props.setForceRefreshBaseData(new Date());
                }
                navigate(-1);
            }
        }
    };

    if (salesZone) {
        return (
            <>
                <Row>
                    <Col>
                        <>
                            <Row>
                                <Col className="d-grid gap-2">
                                    <h2>{name} Settings ({departments.length} Locations)</h2>
                                    <Button
                                        variant="primary"
                                        className="mb-3"
                                        onClick={() => { setSettingsModalShowing(true); }}>
                                        Update Settings
                                    </Button>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <MapControl
                                        mapHeight="1000px"
                                        pinClickCallback={pinClickCallback}
                                        mapClickCallback={mapClickCallback}
                                        onDragCallback={pinDragCallback}
                                        zoom={defaultZoomLevel}
                                        draggablePin={draggablePinPosition && {
                                            latitude: draggablePinPosition.latitude,
                                            longitude: draggablePinPosition.longitude,
                                            radius: mapRadiusMiles
                                        }}
                                        position={draggablePinPosition && {
                                            latitude: draggablePinPosition.latitude,
                                            longitude: draggablePinPosition.longitude
                                        }}
                                        pins={pins}
                                        htmlMarkers={htmlMarkers}
                                        useHeatMap={true}
                                        heatMapOpacity={0.4}
                                        loadingLocations={loadingLocations}
                                        mapZoomCallback={zoomCallback}
                                        circles={
                                            props.baseData?.allZones
                                                .filter(
                                                    s => s.latitude != undefined &&
                                                        s.longitude != undefined &&
                                                        s.distance != undefined &&
                                                        s.salesZoneId !== salesZone.salesZoneId
                                                )
                                                .map(
                                                    s => ({
                                                        latitude: s.latitude || 0,
                                                        longitude: s.longitude || 0,
                                                        radius: s.distance || 0,
                                                        color: "#DD4411",
                                                        opacity: 0.4
                                                    })) || undefined}
                                    />
                                </Col>
                            </Row>

                            <Modal
                                show={settingsModalShowing}
                                id="settingsModal"
                                onHide={() => { setSettingsModalShowing(false); updateSettings(); }}
                                size="xl">
                                <Modal.Header closeButton>
                                    <Modal.Title>Sales Zone Settings<br /><i style={{ fontSize: "16px" }}>(All settings required for a match. - & 0 are ignored)</i></Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <Form>
                                        <Card className="mb-2 mt-2">
                                            <Card.Body>
                                                <Row className="align-items-end">
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Sales Zone Name</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={(newVal) => { setName(newVal.target.value) }}
                                                                    value={name}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Search Radius <i>(Miles)</i></Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={(e) => { setMapRadiusMiles(parseFloat(e.target.value)) }}
                                                                    value={mapRadiusMiles}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Max Records to Pull <i>(Closest first)</i></Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={(e) => { setMapMaxRecords(parseFloat(e.target.value)) }}
                                                                    value={mapMaxRecords}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <Row className="align-items-end">
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Engagement</Form.Label>
                                                            {
                                                                <Form.Select
                                                                    onChange={async (e) => {
                                                                        setFilters({ ...filters, priorities: e.target.value ? [e.target.value] : undefined });
                                                                    }}
                                                                    value={filters.priorities ? filters.priorities[0] : undefined}>
                                                                    <option key={"empty"} value={""}>-</option>
                                                                    {
                                                                        priorities.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                                    }
                                                                </Form.Select>
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Action</Form.Label>
                                                            {
                                                                <Form.Select
                                                                    onChange={async (e) => {
                                                                        setFilters({ ...filters, actions: e.target.value ? [e.target.value] : undefined });
                                                                    }}
                                                                    value={filters.actions ? filters.actions[0] : undefined}>
                                                                    <option key={"empty"} value={""}>-</option>
                                                                    {
                                                                        actions.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                                    }
                                                                </Form.Select>
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Status</Form.Label>
                                                            {
                                                                <Form.Select
                                                                    onChange={async (e) => {
                                                                        setFilters({ ...filters, statuses: e.target.value ? [e.target.value] : undefined });
                                                                    }}
                                                                    value={filters.statuses ? filters.statuses[0] : undefined}>
                                                                    <option key={"empty"} value={""}>-</option>
                                                                    {
                                                                        statuses.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                                    }
                                                                </Form.Select>
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <Row className="align-items-end">
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Dept Type</Form.Label>
                                                            {
                                                                <Form.Select
                                                                    onChange={async (e) => {
                                                                        setFilters({ ...filters, deptTypes: e.target.value ? [e.target.value] : undefined });
                                                                    }}
                                                                    value={filters.deptTypes ? filters.deptTypes[0] : undefined}>
                                                                    <option key={"empty"} value={""}>-</option>
                                                                    {
                                                                        [
                                                                            "Career",
                                                                            "Mostly career",
                                                                            "Mostly volunteer",
                                                                            "Volunteer"
                                                                        ].map(s => <option key={s} value={s}>{s}</option>)
                                                                    }
                                                                </Form.Select>
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Specialization</Form.Label>
                                                            {
                                                                <Form.Select
                                                                    onChange={async (e) => {
                                                                        setFilters({ ...filters, specializations: e.target.value ? [e.target.value] : undefined });
                                                                    }}
                                                                    value={filters.specializations ? filters.specializations[0] : undefined}>
                                                                    <option key={"empty"} value={""}>-</option>
                                                                    {
                                                                        [
                                                                            "Air Transport",
                                                                            "Amb Transport",
                                                                            "Arson Inv",
                                                                            "Confined Space",
                                                                            "Drone",
                                                                            "EMT Basic",
                                                                            "EMT Intermediate",
                                                                            "EMT Paramedic",
                                                                            "Fire Boat",
                                                                            "HAZMAT",
                                                                            "Heavy Rescue",
                                                                            "Hi-Angle/Rope Rescue",
                                                                            "Ice Rescue",
                                                                            "Light/Medium Rescue",
                                                                            "SCUBA/Dive Rescue",
                                                                            "Water Rescue",
                                                                            "Wildland/Forest Protection",
                                                                        ].map(s => <option key={s} value={s}>{s}</option>)
                                                                    }
                                                                </Form.Select>
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Primary Emergency Mgmt</Form.Label>
                                                            {
                                                                <Form.Select
                                                                    onChange={async (e) => {
                                                                        setFilters({ ...filters, primaryAgencyForEmergencyMgmt: e.target.value != "" ? e.target.value == "true" : undefined });
                                                                    }}
                                                                    value={filters.primaryAgencyForEmergencyMgmt != undefined ? filters.primaryAgencyForEmergencyMgmt ? "true" : "false" : ""}>
                                                                    <option key={"empty"} value={""}>-</option>
                                                                    {
                                                                        ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                                    }
                                                                </Form.Select>
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <Row className="align-items-end">
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Min Station Count</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, minNumberOfStations: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.minNumberOfStations || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Max Station Count</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, maxNumberOfStations: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.maxNumberOfStations || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>

                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Min Active Career</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, minActiveFirefightersCareer: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.minActiveFirefightersCareer || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Max Active Career</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, maxActiveFirefightersCareer: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.maxActiveFirefightersCareer || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <Row className="align-items-end">
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Min Active Volunteer</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, minActiveFirefightersVolunteer: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.minActiveFirefightersVolunteer || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Max Active Volunteer</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, maxActiveFirefightersVolunteer: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.maxActiveFirefightersVolunteer || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>

                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Min Paid Per Call</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, minActiveFirefightersPaidPerCall: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.minActiveFirefightersPaidPerCall || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Max Paid Per Call</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, maxActiveFirefightersPaidPerCall: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.maxActiveFirefightersPaidPerCall || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <Row className="align-items-end">
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Min Equipment</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, minEquipment: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.minEquipment || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Max Equipment</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, maxEquipment: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.maxEquipment || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Min HRAs</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, minHRAs: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.minHRAs || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Max HRAs</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, maxHRAs: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.maxHRAs || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Min AirCards</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, minAirCards: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.minAirCards || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Max AirCards</Form.Label>
                                                            {
                                                                <Form.Control
                                                                    type="text"
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setFilters({ ...filters, maxAirCards: e.target.value && e.target.value !== "0" ? parseInt(e.target.value) : undefined });
                                                                        }
                                                                    }
                                                                    value={filters.maxAirCards || 0}
                                                                />
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Form>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        variant="primary"
                                        onClick={() => { setSettingsModalShowing(false); updateSettings(); }}>
                                        Apply
                                    </Button>
                                </Modal.Footer>
                            </Modal>
                        </>
                    </Col>
                </Row>

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    globalContext.roles.some(r => r === "Web.SalesZones.Lookup") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the SalesZone, please try again.</h1>
            </>
        );
    }
}

export default SalesZonesDetail;
